import { useCallback } from 'react';
import { log } from 'log';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import * as actions from 'js/hooks/auth/store/actions';
import * as pas from 'js/hooks/auth/pas';

import { removeCookie } from 'js/utils/cookie.util';
import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { resetToDefault } from 'js/hooks/user/store/actions';
import { LOGGED_IN_USER_STATUS } from 'js/app/hooks/auth/constants';
import { useNavigateToHome } from 'js/app/hooks/navigate/useNavigateToHome';
import { useCurrentItemMenu } from 'js/app/hooks/menu/useMenu';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useConfirmationPopup } from 'js/components/common/popups/ConfirmationPopup';
import { resetResponsibleGaming } from '@interlayer/js/app/store/reducers/ResponsibleGaming/actions';
import { resetRecentlyGames } from '@interlayer/js/app/store/reducers/recentlyGames/actions';
import { useUserSelector } from '@core/js/store/hooks';

export const useLogout = () => {
    const dispatch = useDispatch();
    const sessionToken = useUserSelector(prop('sessionToken'));

    // buzz start
    const navigateToHome = useNavigateToHome();
    const currentItemMenu = useCurrentItemMenu();
    const showConfirmationPopup = useConfirmationPopup();

    const [
        allSessions,
        realMode,
        behaviour,
        invalidateRememberMe
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.logoutParamAllSessions,
        DYNAMIC_TAG_NAMES.logoutParamRealMode,
        DYNAMIC_TAG_NAMES.logoutParamBehaviour,
        DYNAMIC_TAG_NAMES.logoutParamInvalidateRememberMe
    ]);
    // buzz end

    return useCallback(({ isQuiet, isManual = true, onDecline, onlyLogout, onClose, afterLogout, suffix } = {}) => {
        const logoutAction = () => {
            dispatch(actions.setIsLogoutManual(isManual));
            dispatch(actions.setIsLogoutInProcess(true));
            dispatch(resetToDefault());
            dispatch(resetRecentlyGames());
            dispatch(resetResponsibleGaming());

            removeCookie(LOGGED_IN_USER_STATUS, { path: '/' });

            if (afterLogout) {
                afterLogout();
            }

            if (currentItemMenu && !currentItemMenu.roles.includes('GUEST')) {
                navigateToHome();
            }

            (async () => {
                try {
                    await pas.setLoginType(null);

                    if (!isQuiet || sessionToken) {
                        // do a regular logout to clear everything, all sessions
                        // sessionToken check is needed to do logout in a separate tab
                        await pas.logout({ allSessions, realMode, behaviour, invalidateRememberMe });
                    }
                } catch (err) {
                    log('info', err);
                } finally {
                    dispatch(actions.setIsLogoutInProcess(false));
                }
            })();
        };

        const transactionSuffix = suffix ? `.${suffix}` : '';

        if (isQuiet || onlyLogout) {
            return logoutAction();
        }

        return showConfirmationPopup({
            title: `playtech.mobile.system.logout${transactionSuffix}.title`,
            isModal: false,
            withLogo: true,
            onClose,
            onAccept: logoutAction,
            onDecline,
            acceptBtnText: `playtech.mobile.system.logout${transactionSuffix}.accept`,
            declineBtnText: `playtech.mobile.system.logout${transactionSuffix}.cancel`,
            content: `playtech.mobile.system.logout${transactionSuffix}.text`
        });
    }, [
        allSessions, behaviour, currentItemMenu, dispatch, invalidateRememberMe, navigateToHome, realMode, sessionToken,
        showConfirmationPopup
    ]);
};
